import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  fetchPrice,
  fetchRate,
  useBankAccounts,
  useCan,
  useCompanies,
  useContact,
  useContacts,
  useCreateContact,
  useCreateDraft,
  useDocument,
  useDocumentAction,
  useDocuments,
  useFeatureFlag,
  useIssueDocument,
  useNumberSeries,
  useUpdateDraft,
} from '../api/hooks';
import type {
  AresContact,
  Contact,
  DocType,
  Document,
  IssueResponse,
  PaymentMethod,
} from '../api/types';
import { I } from '../components/Icons';
import {
  Btn,
  Chip,
  Field,
  H2,
  SelectField,
  Surface,
  SwitchRow,
  TextareaField,
} from '../components/ui';
import { useT } from '../i18n';
import {
  FLAGGED_TYPES,
  isPayable,
  isReceived,
  MANUAL_TYPES,
  METHODS,
  TYPES,
  totalsOf,
  vsFor,
} from '../lib/doc';
import { addDays, CURRENCIES, czk, money, todayIso } from '../lib/format';
import { useCompanyFilter } from '../state/company';
import { useTopbar } from '../state/topbar';
import { useUi } from '../state/ui';
import { AresSheet } from './AresSheet';
import {
  groupDescription,
  type StockPick,
  type StockPickInitial,
  StockPickSheet,
} from './StockPickSheet';

interface DraftItem {
  text: string;
  /** popisek pod názvem na dokladu, např. „stav A“ */
  description: string;
  qty: string;
  unit: string;
  price: string;
  vat: number;
  /** zvláštní režim §90 – použité zboží jde v sazbě 0 a doklad dostane větu o režimu */
  margin: boolean;
  /** přenesená daňová povinnost §92f */
  rc: boolean;
  /** kód předmětu plnění pro kontrolní hlášení */
  rcCode: number;
  /** zboží z katalogu skladu */
  productId?: string | null;
  variantId?: string | null;
  /** kusy ze skladu, které se při vystavení označí jako prodané; jejich IMEI jdou do přílohy */
  stockUnitIds?: string[];
  /** skupina skladu (zboží + varianta + stav), ze které kusy jsou – pro úpravu výběru */
  stockGroup?: Omit<StockPickInitial, 'unitIds'>;
  /** nákupní ceny vybraných kusů (id kusu → Kč) – jen pro informaci, na doklad se neposílají */
  unitCosts?: Record<string, number>;
  /** nákupní cena za jednotku u množstevního zboží – jen pro informaci */
  costEach?: number;
}

/** Nákup celé položky (jen informace pro toho, kdo doklad vystavuje). */
function lineCost(x: DraftItem): number | null {
  if (x.unitCosts && Object.keys(x.unitCosts).length) {
    return Object.values(x.unitCosts).reduce((a, b) => a + b, 0);
  }
  if (x.costEach != null) return x.costEach * (Number(x.qty.replace(',', '.')) || 0);
  return null;
}

/**
 * Kódy předmětu plnění pro kontrolní hlášení, oddíl A.1. Nejčastější u prodeje
 * telefonů je 14; tablety a notebooky mají 16, herní konzole 17.
 */
const RC_CODES: [number, string][] = [
  [14, 'Mobilní telefony'],
  [16, 'Notebooky, tablety'],
  [17, 'Herní konzole'],
  [15, 'Integrované obvody'],
  [13, 'Kovy'],
  [4, 'Stavební a montážní práce'],
];

/** Dočasný režim §92f se použije až nad tímto základem daně (nař. vlády 361/2014 Sb.). */
const RC_LIMIT = 100000;

/** Typy dokladů v rozbalovacím seznamu po skupinách; nezařazené spadnou do „Ostatní“. */
const TYPE_GROUPS: [string, DocType[]][] = [
  ['Prodej', ['FV', 'ZF', 'PF', 'DB', 'VR']],
  ['Nabídky a objednávky', ['NA', 'OP']],
  ['Sklad', ['DL', 'VY']],
  ['Nákup', ['OV', 'PR', 'FP']],
];
function typeGroups(choices: DocType[]): { label: string; types: DocType[] }[] {
  const known = new Set(TYPE_GROUPS.flatMap(([, t]) => t));
  return [
    ...TYPE_GROUPS.map(([label, types]) => ({
      label,
      types: types.filter((t) => choices.includes(t)),
    })),
    { label: 'Ostatní', types: choices.filter((t) => !known.has(t)) },
  ].filter((g) => g.types.length);
}

const emptyItem = (): DraftItem => ({
  text: '',
  description: '',
  qty: '1',
  unit: 'ks',
  price: '',
  vat: 21,
  margin: false,
  rc: false,
  rcCode: 14,
});

export function NewDocument() {
  const [loadedDraft, setLoadedDraft] = useState<string | null>(null);
  const t = useT();
  useTopbar(loadedDraft ? t('doc.draft') : t('doc.new'));
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { activeCompanyId: filter } = useCompanyFilter();
  const { data: companies = [] } = useCompanies();
  const { openSheet, closeSheet, toast } = useUi();
  const issue = useIssueDocument();
  const createDraft = useCreateDraft();
  const draftId = params.get('draft') ?? '';
  const updateDraft = useUpdateDraft(draftId);
  const { data: existingDraft } = useDocument(draftId);
  const createContact = useCreateContact();
  const issueDraft = useDocumentAction(draftId);

  const [type, setType] = useState<DocType>('FV');
  const [companyId, setCompanyId] = useState<string>('');
  const [contact, setContact] = useState<Contact | null>(null);
  const [cq, setCq] = useState('');
  const [issuedAt, setIssuedAt] = useState(todayIso());
  const [dueAt, setDueAt] = useState(addDays(todayIso(), 14));
  const [dueDays, setDueDays] = useState<number | null>(14);
  const [method, setMethod] = useState<PaymentMethod>('transfer');
  /** jazyk dokladu (fáze 14): prázdné = podle partnera a firmy */
  const [docLocale, setDocLocale] = useState('');
  const [paidNow, setPaidNow] = useState(true);
  const [items, setItems] = useState<DraftItem[]>([emptyItem()]);
  const [note, setNote] = useState('');
  const [relatedId, setRelatedId] = useState<string>('');
  const [externalNumber, setExternalNumber] = useState('');
  const [externalVs, setExternalVs] = useState('');
  const [currency, setCurrency] = useState<string>('');
  const [rate, setRate] = useState<{ rate: string; source: string } | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  const [manualRate, setManualRate] = useState('');

  // firma: vybraná v přepínači, jinak první
  useEffect(() => {
    if (companyId || !companies.length) return;
    const preferred = companies.find((s) => s.id === filter) ?? companies[0];
    if (preferred) setCompanyId(preferred.id);
  }, [companies, filter, companyId]);

  const S = companies.find((s) => s.id === companyId);
  const { data: series = [] } = useNumberSeries(companyId);
  const nextNumber = series.find((x) => x.docType === type)?.nextNumber ?? '…';
  // typy za feature flagem (nabídky, objednávky, nákup) se nabídnou jen zapnuté
  const salesOrders = useFeatureFlag('sales_orders', companyId || null);
  const purchasing = useFeatureFlag('purchasing', companyId || null);
  const typeChoices: DocType[] = [
    ...MANUAL_TYPES,
    ...(salesOrders ? (FLAGGED_TYPES.sales_orders ?? []) : []),
    ...(purchasing ? (FLAGGED_TYPES.purchasing ?? []) : []),
  ];
  // odběratelé jen z firmy, která doklad vystavuje
  const { data: matches = [] } = useContacts(companyId || null, cq.trim());
  const [moreContacts, setMoreContacts] = useState(false);
  const presetId = params.get('contact') ?? '';
  const { data: preset, isError: presetError } = useContact(presetId);
  // předvolený odběratel (?contact=) se použije jen jednou; do té doby se nabídka kontaktů neukazuje
  const [presetUsed, setPresetUsed] = useState(false);
  const { data: docs = [] } = useDocuments({ companyId: companyId || undefined });
  const { data: draftContact } = useContact(existingDraft?.contactId ?? '');

  // otevřený koncept: formulář se předvyplní jednou, pak už patří uživateli
  useEffect(() => {
    if (!existingDraft || loadedDraft === existingDraft.id || !draftContact) return;
    if (existingDraft.status !== 'draft') return;
    setLoadedDraft(existingDraft.id);
    setCompanyId(existingDraft.companyId);
    setType(existingDraft.type);
    setContact(draftContact);
    setIssuedAt(existingDraft.issuedAt);
    setDueAt(existingDraft.dueAt);
    setDueDays(null);
    setMethod(existingDraft.paymentMethod);
    setDocLocale(existingDraft.locale ?? '');
    setNote(existingDraft.note);
    setExternalNumber(existingDraft.externalNumber ?? '');
    setExternalVs(existingDraft.variableSymbol ?? '');
    setCurrency(existingDraft.currency ?? '');
    setRelatedId(existingDraft.relatedDocumentId ?? '');
    setItems(
      existingDraft.items.length
        ? existingDraft.items.map((i) => ({
            ...emptyItem(),
            text: i.text,
            description: i.description ?? '',
            qty: i.quantity,
            unit: i.unit,
            price: i.unitPrice,
            vat: i.vatRate,
            margin: i.marginScheme,
            rc: !!i.reverseCharge,
            rcCode: i.reverseChargeCode ?? 14,
            productId: i.productId ?? null,
            variantId: i.variantId ?? null,
          }))
        : [emptyItem()],
    );
  }, [existingDraft, draftContact, loadedDraft]);

  const pickContact = (c: Contact) => {
    setContact(c);
    setCq('');
    if (c.defaultDueDays != null) setDue(c.defaultDueDays);
  };
  // po přepnutí firmy nesmí zůstat odběratel z adresáře té předchozí
  useEffect(() => {
    if (contact && companyId && contact.companyId !== companyId) setContact(null);
  }, [companyId, contact]);
  // předvyplnění z ?contact= – jen jednou po načtení předvolby
  // biome-ignore lint/correctness/useExhaustiveDependencies: záměrně reaguje jen na preset
  useEffect(() => {
    if (preset && !contact) {
      pickContact(preset);
      setPresetUsed(true);
    }
  }, [preset]);

  const setDue = (n: number) => {
    setDueDays(n);
    setDueAt(addDays(issuedAt, n));
  };
  const changeIssued = (v: string) => {
    setIssuedAt(v);
    if (dueDays != null) setDueAt(addDays(v, dueDays));
  };

  const homeCurrency = S?.currency ?? 'CZK';
  const docCurrency = currency || homeCurrency;
  const { data: bankAccounts = [] } = useBankAccounts(companyId || null);
  const multiCurrency = useFeatureFlag('multi_currency', companyId || null);
  // cizí měnu nabídneme, když ji firma má na účtu nebo má zapnutý flag
  const currencyChoices = [
    homeCurrency,
    ...new Set([...bankAccounts.map((b) => b.currency), ...(multiCurrency ? CURRENCIES : [])]),
  ].filter((c, i, arr) => c && arr.indexOf(c) === i);
  const showCurrency = currencyChoices.length > 1;
  // kurz k datu vystavení; chybějící kurz jde zadat ručně
  useEffect(() => {
    if (!companyId || docCurrency === homeCurrency) {
      setRate(null);
      setRateError(null);
      return;
    }
    let live = true;
    fetchRate(companyId, homeCurrency, docCurrency, issuedAt)
      .then((r) => {
        if (!live) return;
        setRate(r.rate);
        setRateError(null);
      })
      .catch((err) => {
        if (!live) return;
        setRate(null);
        setRateError(err instanceof ApiError ? err.message : 'Kurz se nepodařilo načíst');
      });
    return () => {
      live = false;
    };
  }, [companyId, docCurrency, homeCurrency, issuedAt]);
  const effectiveRate = manualRate.trim() ? manualRate.replace(',', '.') : (rate?.rate ?? null);

  const isDL = type === 'DL';
  /** doklady bez platby (nabídka, objednávky, příjemka, výdejka, interní) nemají splatnost ani úhradu */
  const noPay = !isPayable(type);
  const received = isReceived(type);
  const access = useCan(companyId || null);
  const canStock = !access.loaded || access.can('stock:read');
  const vatPayer = S?.vatPayer ?? true;
  const cashy = method === 'cash' || method === 'card';
  const totals = useMemo(
    () =>
      totalsOf(
        items.map((x) => ({
          quantity: Number(x.qty.replace(',', '.')) || 0,
          unitPrice: Number(x.price.replace(',', '.')) || 0,
          vatRate: x.margin || x.rc ? 0 : x.vat,
        })),
        vatPayer,
      ),
    [items, vatPayer],
  );
  /**
   * Upozornění na §92f: doklad mezi dvěma plátci nad 100 000 Kč základu, kde zatím
   * žádná položka přenesenou daňovou povinnost nemá. Rozhodnout musí uživatel –
   * záleží na tom, jestli jde o zboží z přílohy č. 6.
   */
  const rcWarning =
    vatPayer &&
    !isDL &&
    !!contact?.dic &&
    Number(totals.base) > RC_LIMIT &&
    !items.some((x) => x.rc);

  /** Cena z ceníku partnera (nebo výchozího) přepíše katalogovou; uživatel ji může změnit. */
  const priceFromList = (productId: string, quantity: string, match: (x: DraftItem) => boolean) =>
    fetchPrice({ companyId, productId, contactId: contact?.id, quantity })
      .then((r) => {
        if (!r.price) return;
        setItems((old) => old.map((x) => (match(x) ? { ...x, price: r.price ?? x.price } : x)));
        if (r.source === 'partner' || r.source === 'default') {
          toast(`Cena z ceníku ${r.priceListCode}`);
        }
      })
      .catch(() => undefined);

  /**
   * Kolečko + nalevo od položky otevře sklad: telefon se vybere po skupině (zboží + stav) a počtem
   * kusů – třeba 20 kusů = 20 různých IMEI v jedné položce „iPhone 16 Pro 128GB“ s popiskem
   * „stav A“. IMEI s celým názvem se vytisknou v příloze dokladu. Příslušenství jen počtem.
   * Nákupní ceny jsou jen informace pod položkou, do dokladu se neposílají.
   */
  const applyPick = (i: number, pick: StockPick) => {
    const cur = items[i];
    if (!cur) return;
    const patch = (next: Partial<DraftItem>) =>
      setItems((old) => old.map((x, idx) => (idx === i ? { ...x, ...next } : x)));
    if (pick.kind === 'units') {
      const g = pick.group;
      const ids = pick.units.map((u) => u.id);
      const qty = String(ids.length);
      const sameGroup =
        cur.stockGroup?.productId === g.productId &&
        cur.stockGroup.variantId === g.variantId &&
        cur.stockGroup.grade === g.grade;
      // úprava výběru stejné skupiny nechá text, popisek i cenu, jak je uživatel upravil
      const listPrice = !(sameGroup && cur.price.trim());
      patch({
        text: sameGroup && cur.text.trim() ? cur.text : g.productName,
        description: sameGroup ? cur.description : groupDescription(g),
        qty,
        unit: g.unit || 'ks',
        price: listPrice ? (g.salePrice ?? cur.price) : cur.price,
        vat: g.vatRate,
        margin: g.marginScheme,
        rc: g.marginScheme ? false : cur.rc,
        productId: g.productId,
        variantId: g.variantId,
        stockUnitIds: ids,
        stockGroup: { productId: g.productId, variantId: g.variantId, grade: g.grade },
        unitCosts: Object.fromEntries(pick.units.map((u) => [u.id, Number(u.purchasePrice) || 0])),
        costEach: undefined,
      });
      const first = ids[0];
      if (listPrice && first) {
        priceFromList(g.productId, qty, (x) => !!x.stockUnitIds?.includes(first));
      }
      return;
    }
    const p = pick.product;
    const qty = String(pick.quantity);
    const same = cur.productId === p.id && !cur.stockUnitIds?.length;
    const listPrice = !(same && cur.price.trim());
    patch({
      text: same && cur.text.trim() ? cur.text : p.name,
      description: same ? cur.description : '',
      qty,
      unit: p.unit,
      price: listPrice ? (p.salePrice ?? cur.price) : cur.price,
      vat: p.vatRate,
      margin: p.marginScheme,
      rc: p.marginScheme ? false : cur.rc,
      productId: p.id,
      variantId: null,
      stockUnitIds: undefined,
      stockGroup: undefined,
      unitCosts: undefined,
      costEach: p.purchasePrice ? Number(p.purchasePrice) || 0 : undefined,
    });
    if (listPrice) {
      priceFromList(p.id, qty, (x) => x.productId === p.id && !x.stockUnitIds?.length);
    }
  };

  /** Počet kusů s IMEI se mění jen ve skladu (pole množství ho otevře), aby seděl s přílohou. */
  const openStock = (i: number) => {
    const x = items[i];
    if (!x || !companyId) return;
    const exclude = new Set(items.flatMap((y, idx) => (idx === i ? [] : (y.stockUnitIds ?? []))));
    const initial: StockPickInitial | undefined =
      x.stockGroup && x.stockUnitIds?.length
        ? { ...x.stockGroup, unitIds: x.stockUnitIds }
        : undefined;
    openSheet(
      <StockPickSheet
        companyId={companyId}
        initial={initial}
        exclude={exclude}
        onCancel={closeSheet}
        onDone={(pick) => {
          closeSheet();
          applyPick(i, pick);
        }}
      />,
    );
  };
  const stockOn = !received && !!companyId && canStock;

  const relatedCandidates = docs.filter(
    (d) =>
      contact &&
      d.contactId === contact.id &&
      d.status !== 'cancelled' &&
      (type === 'DB' ? d.type === 'FV' : d.type === 'FV' || d.type === 'PF'),
  );

  const setItem = (i: number, k: keyof DraftItem, v: string | number | boolean) =>
    setItems((old) => old.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeItem = (i: number) =>
    setItems((old) => (old.length > 1 ? old.filter((_, idx) => idx !== i) : [emptyItem()]));

  const useTyped = () => {
    const name = cq.trim();
    if (!name) return;
    createContact.mutate(
      { companyId, name },
      {
        onSuccess: ({ contact: c }) => {
          pickContact(c);
          toast(`Kontakt ${name} založen`);
        },
        onError: (err) => toast(err.message),
      },
    );
  };

  const fromAres = () =>
    openSheet(
      <AresSheet
        onCancel={closeSheet}
        onFound={(a: AresContact) => {
          closeSheet();
          createContact.mutate(
            {
              companyId,
              name: a.name,
              ico: a.ico,
              dic: a.dic,
              street: a.street,
              city: a.city,
              zip: a.zip,
            },
            {
              onSuccess: ({ contact: c }) => {
                pickContact(c);
                toast(`${c.name} načteno z ARES`);
              },
              onError: (err) => toast(err.message),
            },
          );
        }}
      />,
    );

  /** Tělo požadavku sdílí vystavení i koncept; koncept smí být bez položek. */
  const buildPayload = (forDraft: boolean) => {
    if (!S) return null;
    if (!contact) {
      toast(received ? 'Vyber dodavatele' : 'Vyber odběratele');
      return null;
    }
    const clean = items
      .map((x) => ({
        text: x.text.trim(),
        description: x.description.trim() || undefined,
        quantity: x.qty.trim().replace(',', '.') || '1',
        unit: x.unit.trim() || 'ks',
        unitPrice: isDL ? '0' : x.price.trim().replace(',', '.') || '0',
        vatRate: x.margin || x.rc ? 0 : x.vat,
        marginScheme: x.margin,
        reverseCharge: x.rc,
        reverseChargeCode: x.rc ? x.rcCode : null,
        productId: x.productId ?? null,
        variantId: x.variantId ?? null,
        stockUnitIds: x.stockUnitIds ?? [],
      }))
      .filter((x) => x.text);
    if (!clean.length && !forDraft) {
      toast('Přidej aspoň jednu položku');
      return null;
    }
    if (type === 'FP' && !externalNumber.trim() && !forDraft) {
      toast('Zadej číslo faktury dodavatele');
      return null;
    }
    return {
      companyId: S.id,
      contactId: contact.id,
      type,
      issuedAt,
      ...(noPay ? {} : { dueAt }),
      paymentMethod: noPay ? ('transfer' as PaymentMethod) : method,
      locale: docLocale || undefined,
      note: note.trim(),
      relatedDocumentId: (type === 'DB' || type === 'DL') && relatedId ? relatedId : null,
      paidNow: !noPay && cashy && paidNow,
      ...(type === 'FP'
        ? { externalNumber: externalNumber.trim() || null, variableSymbol: externalVs.trim() }
        : {}),
      ...(docCurrency !== homeCurrency
        ? {
            currency: docCurrency,
            ...(manualRate.trim() ? { exchangeRate: manualRate.replace(',', '.') } : {}),
          }
        : {}),
      items: clean,
    };
  };

  const afterIssue = ({ document, approvalRequired }: IssueResponse) => {
    navigate(`/doklady/${document.id}`, { replace: true });
    toast(
      approvalRequired
        ? 'Doklad je nad limit – uložen jako koncept a čeká na schválení'
        : `${TYPES[document.type].label} ${document.number} ${TYPES[document.type].done}`,
    );
  };
  const onError = (err: unknown) =>
    toast(err instanceof ApiError ? err.message : 'Vystavení se nezdařilo');

  const submit = () => {
    const payload = buildPayload(false);
    if (!payload) return;
    if (draftId && loadedDraft) {
      // koncept: nejdřív uložit poslední změny, pak vystavit
      updateDraft.mutate(payload, {
        onSuccess: () => issueDraft.mutate('issue', { onSuccess: afterIssue, onError }),
        onError,
      });
      return;
    }
    issue.mutate(payload, { onSuccess: afterIssue, onError });
  };

  const saveDraft = () => {
    const payload = buildPayload(true);
    if (!payload) return;
    const done = ({ document }: { document: Document }) => {
      navigate(`/doklady/${document.id}`, { replace: true });
      toast('Koncept uložen');
    };
    if (draftId && loadedDraft) updateDraft.mutate(payload, { onSuccess: done, onError });
    else createDraft.mutate(payload, { onSuccess: done, onError });
  };

  return (
    <>
      <div className="form-cols">
        <div>
          {/* typ dokladu: rozbalovací seznam (na iPhonu nativní kolečko) místo tří řádků čipů */}
          <div className="type-pick">
            <span className="type-pick-ic" aria-hidden="true">
              {I.docs}
            </span>
            <span className="type-pick-txt">
              <span className="k">Typ dokladu</span>
              <span className="type-pick-val">{TYPES[type].label}</span>
            </span>
            <span className="type-pick-chev" aria-hidden="true">
              {I.chev}
            </span>
            <select
              aria-label="Typ dokladu"
              value={type}
              onChange={(e) => setType(e.target.value as DocType)}
            >
              {typeGroups(typeChoices.includes(type) ? typeChoices : [...typeChoices, type]).map(
                (g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.types.map((k) => (
                      <option key={k} value={k}>
                        {TYPES[k].label}
                      </option>
                    ))}
                  </optgroup>
                ),
              )}
            </select>
          </div>
          <Surface className="mt">
            <div className="row">
              <div className="grow">
                <div className="k">Vystavuje</div>
                <div className="seg small">
                  {companies.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      className={companyId === s.id ? 'on' : ''}
                      onClick={() => setCompanyId(s.id)}
                    >
                      {s.shortName}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="row grid3">
              <div>
                <div className="k">Číslo dokladu</div>
                <div className="l1">{nextNumber}</div>
              </div>
              <div>
                <div className="k">Var. symbol</div>
                <div>
                  {type === 'FP'
                    ? externalVs || '—'
                    : nextNumber === '…'
                      ? '…'
                      : vsFor(type, nextNumber) || '—'}
                </div>
              </div>
              <div>
                <div className="k">DPH</div>
                <div>{vatPayer ? 'plátce' : 'neplátce'}</div>
              </div>
            </div>
            {showCurrency && (
              <div className="row">
                <div className="grow">
                  <div className="k">Měna</div>
                  <div className="seg small">
                    {currencyChoices.map((c) => (
                      <button
                        type="button"
                        key={c}
                        className={docCurrency === c ? 'on' : ''}
                        onClick={() => setCurrency(c === homeCurrency ? '' : c)}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                  {docCurrency !== homeCurrency && (
                    <div className="l2 mt">
                      {rate && !manualRate.trim() ? (
                        <span>
                          kurz 1 {docCurrency} = {rate.rate} {homeCurrency} (
                          {rate.source.toUpperCase()})
                        </span>
                      ) : rateError && !manualRate.trim() ? (
                        <span className="bad">{rateError}</span>
                      ) : null}
                      <input
                        aria-label="Vlastní kurz"
                        inputMode="decimal"
                        placeholder={`vlastní kurz (${homeCurrency} za 1 ${docCurrency})`}
                        value={manualRate}
                        onChange={(e) => setManualRate(e.target.value)}
                        style={{ maxWidth: 220 }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </Surface>

          <H2>{received ? t('doc.supplier') : t('doc.customer')}</H2>
          <Surface className="pad">
            {contact ? (
              <div className="rowx">
                <div className="grow">
                  <div className="l1">{contact.name}</div>
                  <div className="l2">{contact.ico ? `IČO ${contact.ico}` : 'soukromá osoba'}</div>
                </div>
                <button type="button" className="link" onClick={() => setContact(null)}>
                  Změnit
                </button>
              </div>
            ) : (
              <>
                <div className="search in">
                  {I.search}
                  <input
                    aria-label="Hledat odběratele"
                    placeholder="Hledej v kontaktech nebo napiš jméno"
                    value={cq}
                    onChange={(e) => setCq(e.target.value)}
                  />
                </div>
                {/* s předvoleným odběratelem (?contact=) nabídku neukazovat, dokud se nepoužije */}
                {!(presetId && !presetUsed && !presetError) && (
                  <ContactQuickPick
                    companyId={companyId}
                    received={received}
                    query={cq.trim()}
                    matches={matches}
                    more={moreContacts}
                    onMore={() => setMoreContacts(true)}
                    onPick={pickContact}
                  />
                )}
                <div className="two mt">
                  <Btn sm onClick={fromAres}>
                    Načíst z ARES
                  </Btn>
                  <Btn sm onClick={useTyped} disabled={!cq.trim()}>
                    Nový kontakt „{cq.trim() || '…'}“
                  </Btn>
                </div>
              </>
            )}
            {type === 'FP' && (
              <div className="two mt">
                <Field
                  id="nd-ext"
                  label="Číslo faktury dodavatele"
                  value={externalNumber}
                  onChange={(e) => setExternalNumber(e.target.value)}
                  required
                />
                <Field
                  id="nd-extvs"
                  label="Variabilní symbol"
                  inputMode="numeric"
                  value={externalVs}
                  onChange={(e) => setExternalVs(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            )}
            {(type === 'DB' || type === 'DL') && contact && (
              <SelectField
                label={type === 'DB' ? 'K faktuře' : 'K dokladu'}
                value={relatedId}
                onChange={(e) => setRelatedId(e.target.value)}
              >
                <option value="">bez vazby</option>
                {relatedCandidates.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.number}, {czk(d.totalAmount)}
                  </option>
                ))}
              </SelectField>
            )}
          </Surface>

          {noPay ? (
            <>
              <H2>{t('doc.dates')}</H2>
              <Surface className="pad">
                <Field
                  id="nd-issued"
                  label="Vystaveno"
                  type="date"
                  className="m0"
                  value={issuedAt}
                  onChange={(e) => changeIssued(e.target.value)}
                />
              </Surface>
            </>
          ) : (
            <>
              <H2>{t('doc.terms')}</H2>
              <Surface className="pad">
                <div className="two">
                  <Field
                    id="nd-issued"
                    label="Vystaveno"
                    type="date"
                    value={issuedAt}
                    onChange={(e) => changeIssued(e.target.value)}
                  />
                  <Field
                    id="nd-due"
                    label="Splatnost"
                    type="date"
                    value={dueAt}
                    onChange={(e) => {
                      setDueAt(e.target.value);
                      setDueDays(null);
                    }}
                  />
                </div>
                <div className="chips">
                  {[7, 14, 30].map((n) => (
                    <Chip key={n} on={dueDays === n} onClick={() => setDue(n)}>
                      {n} dní
                    </Chip>
                  ))}
                </div>
                <SelectField
                  label="Způsob úhrady"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                >
                  {(Object.keys(METHODS) as PaymentMethod[]).map((k) => (
                    <option key={k} value={k}>
                      {METHODS[k]}
                    </option>
                  ))}
                </SelectField>
                <SelectField
                  id="doc-locale"
                  label={t('doc.language')}
                  value={docLocale}
                  onChange={(e) => setDocLocale(e.target.value)}
                >
                  <option value="">
                    {contact?.locale
                      ? t(`doc.language.${contact.locale}` as 'doc.language.cs')
                      : t('doc.language.cs')}{' '}
                    ({t('doc.language.auto')})
                  </option>
                  {(['cs', 'en', 'cs-en', 'sk', 'de', 'pl'] as const).map((l) => (
                    <option key={l} value={l}>
                      {t(`doc.language.${l}` as 'doc.language.cs')}
                    </option>
                  ))}
                </SelectField>
                {cashy && (
                  <SwitchRow
                    title="Uhrazeno při vystavení"
                    subtitle={`Rovnou se zapíše platba ${method === 'card' ? 'kartou' : 'v hotovosti'}`}
                    checked={paidNow}
                    onChange={setPaidNow}
                  />
                )}
              </Surface>
            </>
          )}
        </div>
        <div>
          <H2>{t('doc.items')}</H2>
          <Surface className="pad">
            {items.map((x, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: položky nemají id, pořadí je jejich identita
              <div className="item" key={i}>
                <div className={`item-main${stockOn ? '' : ' solo'}`}>
                  {stockOn && (
                    <button
                      type="button"
                      className={`item-stock${x.productId ? ' on' : ''}`}
                      aria-label={`Ze skladu – položka ${i + 1}`}
                      title="Vybrat ze skladu"
                      onClick={() => openStock(i)}
                    >
                      {I.plus}
                    </button>
                  )}
                  <div className="item-text">
                    <input
                      aria-label={`Položka ${i + 1}`}
                      placeholder="Co fakturuješ"
                      value={x.text}
                      onChange={(e) => setItem(i, 'text', e.target.value)}
                    />
                    <input
                      className="item-desc-in"
                      aria-label={`Popisek položky ${i + 1}`}
                      placeholder="Popisek, např. stav A"
                      value={x.description}
                      onChange={(e) => setItem(i, 'description', e.target.value)}
                    />
                  </div>
                </div>
                <div className={`itrow ${isDL ? 'dl' : vatPayer ? '' : 'novat'}`}>
                  <input
                    aria-label="Množství"
                    inputMode="decimal"
                    placeholder="Množ."
                    value={x.qty}
                    readOnly={!!x.stockUnitIds?.length}
                    onClick={x.stockUnitIds?.length ? () => openStock(i) : undefined}
                    onChange={(e) => setItem(i, 'qty', e.target.value)}
                  />
                  <input
                    aria-label="Měrná jednotka"
                    placeholder="j."
                    value={x.unit}
                    onChange={(e) => setItem(i, 'unit', e.target.value)}
                  />
                  {!isDL && (
                    <input
                      aria-label="Cena za jednotku"
                      inputMode="decimal"
                      placeholder="Cena za j."
                      value={x.price}
                      onChange={(e) => setItem(i, 'price', e.target.value)}
                    />
                  )}
                  {vatPayer && !isDL && (
                    <select
                      aria-label="Sazba DPH"
                      value={x.margin || x.rc ? 0 : x.vat}
                      disabled={x.margin || x.rc}
                      onChange={(e) => setItem(i, 'vat', Number(e.target.value))}
                    >
                      {[21, 12, 0].map((r) => (
                        <option key={r} value={r}>
                          {r} %
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    className="x"
                    onClick={() => removeItem(i)}
                    aria-label="Odebrat položku"
                  >
                    ×
                  </button>
                </div>
                {(lineCost(x) != null || !!x.stockUnitIds?.length) && (
                  <div className="item-cost">
                    {!!x.stockUnitIds?.length && (
                      <button type="button" className="imei-chip" onClick={() => openStock(i)}>
                        {x.stockUnitIds.length}× IMEI – v příloze dokladu
                      </button>
                    )}
                    {lineCost(x) != null && (
                      <span className="price-tag">nákup {czk(lineCost(x) ?? 0)}</span>
                    )}
                    <span className="muted">nákup jen pro tebe – na doklad se netiskne</span>
                  </div>
                )}
                {vatPayer && !isDL && (
                  <div className="item-flags">
                    <label className="item-margin">
                      <input
                        type="checkbox"
                        checked={x.margin}
                        disabled={x.rc}
                        onChange={(e) => setItem(i, 'margin', e.target.checked)}
                      />
                      Použité zboží (§ 90)
                    </label>
                    <label className="item-margin">
                      <input
                        type="checkbox"
                        checked={x.rc}
                        disabled={x.margin}
                        onChange={(e) => setItem(i, 'rc', e.target.checked)}
                      />
                      Přenesená daň. povinnost (§ 92f)
                    </label>
                    {x.rc && (
                      <select
                        aria-label="Kód předmětu plnění"
                        value={x.rcCode}
                        onChange={(e) => setItem(i, 'rcCode', Number(e.target.value))}
                      >
                        {RC_CODES.map(([code, label]) => (
                          <option key={code} value={code}>
                            {code} – {label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            ))}
            {rcWarning && (
              <p className="warn-box">
                Základ daně je {czk(totals.base)} a odběratel je plátce DPH. U mobilních telefonů,
                tabletů, notebooků a herních konzolí nad {czk(RC_LIMIT)} se podle § 92f zákona o DPH
                musí použít přenesená daňová povinnost – zaškrtni ji u příslušných položek.
              </p>
            )}
            <Btn sm full className="mt" onClick={() => setItems((old) => [...old, emptyItem()])}>
              Přidat položku
            </Btn>
            {stockOn && (
              <p className="muted item-hint">
                Tlačítkem + u položky vybereš zboží ze skladu – třeba 20 kusů najednou. IMEI se
                vytisknou v příloze dokladu.
              </p>
            )}
          </Surface>

          <TextareaField
            id="nd-note"
            className="mt"
            label="Poznámka na doklad"
            rows={2}
            placeholder="např. Děkujeme za objednávku"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      <div className="sticky">
        <div>
          <div className="k">{noPay ? 'Položek' : `Celkem${vatPayer ? ' s DPH' : ''}`}</div>
          <div className="amt">
            {noPay ? items.filter((x) => x.text.trim()).length : money(totals.total, docCurrency)}
          </div>
          {!noPay && docCurrency !== homeCurrency && effectiveRate && (
            <div className="l2">
              <span>≈ {money(Number(totals.total) * Number(effectiveRate), homeCurrency)}</span>
            </div>
          )}
        </div>
        {/* na telefonu jeden řádek: kratší popisky, celý název zůstává jako jméno tlačítka */}
        <Btn
          aria-label="Uložit koncept"
          onClick={saveDraft}
          disabled={createDraft.isPending || updateDraft.isPending}
        >
          <span className="wide-only">Uložit koncept</span>
          <span className="narrow-only">Koncept</span>
        </Btn>
        <Btn
          variant="primary"
          aria-label={`Vystavit ${TYPES[type].acc}`}
          onClick={submit}
          disabled={issue.isPending || updateDraft.isPending || issueDraft.isPending}
        >
          <span className="wide-only">Vystavit {TYPES[type].acc}</span>
          <span className="narrow-only">Vystavit</span>
        </Btn>
      </div>
    </>
  );
}

/**
 * Odběratel (u přijatých dodavatel) z kontaktů: bez hledání nejčastější partneři, při psaní
 * výsledky hledání. Celý řádek s kolečkem + vybírá.
 */
function ContactQuickPick({
  companyId,
  received,
  query,
  matches,
  more,
  onMore,
  onPick,
}: {
  companyId: string;
  received: boolean;
  query: string;
  matches: Contact[];
  more: boolean;
  onMore: () => void;
  onPick: (c: Contact) => void;
}) {
  const { data: frequent = [] } = useContacts(
    companyId || null,
    '',
    received ? 'supplier' : 'customer',
    'documents:desc',
  );
  const list = query ? matches : frequent;
  const limit = more ? 25 : 5;
  if (!list.length) return null;
  return (
    <div className="qp-list mt">
      {!query && <div className="qp-h">{received ? 'Častí dodavatelé' : 'Častí odběratelé'}</div>}
      {list.slice(0, limit).map((c) => (
        <button
          type="button"
          key={c.id}
          className="qp-row"
          aria-label={`Vybrat ${c.name}`}
          onClick={() => onPick(c)}
        >
          <span className="grow">
            <span className="l1">{c.name}</span>
            <span className="l2">
              <span>{c.ico ? `IČO ${c.ico}` : 'soukromá osoba'}</span>
              {c.city && <span>{c.city}</span>}
              {c.documentCount > 0 && <span>{c.documentCount} dokl.</span>}
            </span>
          </span>
          <span className="qp-plus" aria-hidden="true">
            {I.plus}
          </span>
        </button>
      ))}
      {list.length > limit && (
        <button type="button" className="link qp-more" onClick={onMore}>
          Zobrazit další
        </button>
      )}
    </div>
  );
}
